import {
  faBarcode,
  faBell,
  faBoxesStacked,
  faBullhorn,
  faCashRegister,
  faChartLine,
  faChartPie,
  faCloudArrowDown,
  faCoins,
  faDesktop,
  faEye,
  faFileArrowUp,
  faFileCsv,
  faFileInvoiceDollar,
  faGaugeHigh,
  faGears,
  faHome,
  faInbox,
  faKey,
  faLaptop,
  faListCheck,
  faMagnifyingGlassDollar,
  faMoneyBillTransfer,
  faPlug,
  faPlus,
  faPrint,
  faRightFromBracket,
  faShop,
  faShopSlash,
  faSitemap,
  faSliders,
  faStore,
  faTableList,
  faTags,
  faToolbox,
  faUsers,
  faUsersGear,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { UserRole } from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { ref } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import {
  ADMIN_ROLES,
  EXTERNAL_ROLES,
  HANDLING_ROLES,
  OFFER_REVIEW_ROLES,
  READONLY_ROLES,
  STORE_MANAGER_ROLES,
} from '../constants';
import DefaultLayout from '../layout/DefaultLayout.vue';
import { useAuth } from '../stores/authStore';
import { useBasketStore } from '../stores/basketStore';
import { registerLoading } from '../stores/loadingStore';
import { usePos } from '../stores/posStore';

declare module 'vue-router' {
  interface RouteMeta {
    icon?: IconDefinition;
    hide?: boolean;
    requiresRole?: UserRole[];
    excludeRoles?: UserRole[];
    requiresSource?: boolean;
    hideInPos?: boolean;
    posOnly?: boolean;
    width?: string;
    fullHeight?: boolean;
    verticalScroll?: boolean;
    horizontalScroll?: boolean;
    section?: 'top' | 'bottom';
    /**
     * Renders the entry as a plain right-aligned link instead of a button-like
     * menu item. Keeps the icon, and sinks the entry to the end of its section.
     */
    textOnly?: boolean;
    public?: boolean;
  }
}

const routeLoading = ref(false);

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: DefaultLayout,
      children: [
        {
          name: 'notFound',
          path: '/:pathMatch(.*)*',
          meta: { hide: true, public: true },
          components: {
            default: () => import('../views/NotFoundView.vue'),
            navbar: () => import('../views/navbar/DefaultNavbar.vue'),
          },
        },
        {
          name: 'forbidden',
          path: '/forbidden',
          meta: { hide: true },
          components: {
            default: () => import('../views/ForbiddenView.vue'),
          },
        },
        {
          name: 'home',
          path: '',
          meta: { icon: faHome },
          components: {
            default: () => import('../views/HomeView.vue'),
          },
        },
        {
          name: 'offers',
          path: 'offer',
          meta: { icon: faBoxesStacked },
          children: [
            {
              name: 'offerNew',
              path: 'new',
              meta: { icon: faPlus, excludeRoles: READONLY_ROLES, requiresSource: true },
              components: {
                default: () => import('../views/offer/create/OfferCreateNew.vue'),
              },
            },
            {
              name: 'offerNewCsv',
              path: 'new-csv',
              meta: { icon: faFileCsv, excludeRoles: READONLY_ROLES },
              components: {
                default: () => import('../views/SimpleRouterView.vue'),
                header: () => import('../views/header/offer/OfferHeaderCreateCsv.vue'),
                footer: () => import('../views/footer/offer/OfferFooterCreateCsv.vue'),
              },
              children: [
                {
                  name: 'offerCreateCsvStart',
                  path: '',
                  meta: { hide: true, excludeRoles: READONLY_ROLES },
                  redirect: { name: 'offerCreateCsvUpload' },
                },
                {
                  name: 'offerCreateCsvUpload',
                  path: 'upload',
                  meta: { hide: true, excludeRoles: READONLY_ROLES },
                  component: () => import('../views/offer/create/OfferCreateCsvUpload.vue'),
                },
                {
                  name: 'offerCreateCsvMapping',
                  path: 'mapping',
                  meta: { hide: true, excludeRoles: READONLY_ROLES },
                  component: () =>
                    import('../views/offer/create/OfferCreateCsvConfigureMapping.vue'),
                },
                {
                  name: 'offerCreateCsvProgress',
                  path: 'progress',
                  meta: { hide: true, excludeRoles: READONLY_ROLES },
                  component: () => import('../views/offer/create/OfferCreateCsvSummary.vue'),
                },
              ],
            },
            {
              name: 'offerOverview',
              path: '',
              meta: { icon: faTableList, fullHeight: true },
              components: {
                default: () => import('../views/offer/overview/OfferOverview.vue'),
                navbar: () => import('../views/navbar/SearchNavbar.vue'),
              },
            },
            {
              name: 'offerImportOverview',
              path: 'imports',
              meta: { icon: faFileArrowUp, fullHeight: true },
              components: {
                default: () => import('../views/offer/overview/OfferImportOverview.vue'),
                navbar: () => import('../views/navbar/SearchNavbar.vue'),
              },
            },
            {
              name: 'offerReviewRequests',
              path: 'review-requests',
              meta: { icon: faListCheck, requiresRole: OFFER_REVIEW_ROLES, fullHeight: true },
              components: {
                default: () => import('../views/offer/overview/OfferReviewOverview.vue'),
                navbar: () => import('../views/navbar/SearchNavbar.vue'),
              },
            },
            {
              path: ':offerId',
              meta: { hide: true },
              children: [
                {
                  path: '',
                  redirect: { name: 'offerSummary' },
                },
                {
                  name: 'offerCompileItems',
                  path: 'compile',
                  meta: { hide: true, excludeRoles: READONLY_ROLES },
                  components: {
                    default: () => import('../views/offer/OfferCompile.vue'),
                    navbar: () => import('../views/navbar/SearchNavbar.vue'),
                    header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                    footer: () => import('../views/footer/offer/OfferFooterCompile.vue'),
                  },
                },
                {
                  name: 'offerReview',
                  path: 'review',
                  meta: { hide: true, horizontalScroll: true, requiresRole: OFFER_REVIEW_ROLES },
                  components: {
                    default: () => import('../views/offer/OfferReview.vue'),
                    header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                    footer: () => import('../views/footer/offer/OfferFooterReview.vue'),
                  },
                },
                {
                  name: 'offerSummary',
                  path: 'summary',
                  meta: { hide: true, horizontalScroll: true },
                  components: {
                    default: () => import('../views/offer/OfferSummary.vue'),
                    header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                    footer: () => import('../views/footer/offer/OfferFooterSummary.vue'),
                  },
                },
                {
                  name: 'offerAccept',
                  path: 'accept',
                  meta: { hide: true, excludeRoles: READONLY_ROLES },
                  components: {
                    header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                    default: () => import('../views/offer/accept/OfferAcceptScreen.vue'),
                    footer: () => import('../views/footer/offer/OfferFooterAcceptScreen.vue'),
                  },
                  beforeEnter: (to, from, next) => {
                    if (to.name !== 'offerAccept') return next();

                    const { addressCompleted, transactionTypeCompleted } = useBasketStore();

                    if (transactionTypeCompleted) {
                      next({ name: 'offerAcceptConfirm', params: to.params });
                    } else if (addressCompleted) {
                      next({ name: 'offerSelectTransactionType', params: to.params });
                    } else {
                      next({ name: 'offerUploadFinalImage', params: to.params });
                    }
                  },
                  children: [
                    {
                      name: 'offerUploadFinalImage',
                      path: 'final-image',
                      meta: { hide: true, excludeRoles: READONLY_ROLES },
                      component: () => import('../views/offer/accept/OfferUploadFinalImage.vue'),
                    },
                    {
                      name: 'offerCollectCustomerInfo',
                      path: 'customer-info',
                      meta: { hide: true, excludeRoles: READONLY_ROLES },
                      component: () => import('../views/offer/accept/OfferCollectCustomerInfo.vue'),
                    },
                    {
                      name: 'offerSelectTransactionType',
                      path: 'transaction-method',
                      meta: { hide: true, excludeRoles: READONLY_ROLES },
                      component: () => import('../views/offer/accept/OfferTransactionSelect.vue'),
                    },
                    {
                      name: 'offerAcceptConfirm',
                      path: 'confirm',
                      meta: { hide: true, excludeRoles: READONLY_ROLES },
                      component: () => import('../views/offer/accept/OfferAcceptConfirm.vue'),
                    },
                  ],
                },
                {
                  name: 'offerTransaction',
                  path: 'transaction',
                  meta: { hide: true, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                  children: [
                    {
                      name: 'offerTransactionProcessCash',
                      path: 'process-cash',
                      meta: { hide: true, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                      components: {
                        default: () =>
                          import('../views/offer/transaction/OfferTransactionCash.vue'),
                        header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                      },
                    },
                    {
                      name: 'offerTransactionProcessGiftCard',
                      path: 'process-voucher',
                      meta: { hide: true, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                      components: {
                        default: () =>
                          import('../views/offer/transaction/OfferTransactionGiftCard.vue'),
                        header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                      },
                    },
                    {
                      name: 'offerTransactionProcessExternalPayment',
                      path: 'process-external-payment',
                      meta: { hide: true, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                      components: {
                        default: () =>
                          import('../views/offer/transaction/OfferTransactionExternalPayment.vue'),
                        header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                      },
                    },
                    {
                      name: 'offerTransactionProcessPayPal',
                      path: 'process-paypal',
                      meta: { hide: true, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                      components: {
                        default: () =>
                          import('../views/offer/transaction/OfferTransactionPayPal.vue'),
                        header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                      },
                    },
                    {
                      name: 'offerTransactionRevertPayPal',
                      path: 'revert-paypal',
                      meta: { hide: true, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                      components: {
                        default: () =>
                          import('../views/offer/transaction/OfferTransactionPayPalRevert.vue'),
                        header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                      },
                    },
                    {
                      name: 'offerTransactionProcessInternalLedgerCash',
                      path: 'process-internal-cash',
                      meta: { hide: true, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                      components: {
                        default: () =>
                          import('../views/offer/transaction/OfferTransactionInternalLedgerCash.vue'),
                        header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                      },
                    },
                    {
                      name: 'offerTransactionRevertCash',
                      path: 'revert-cash',
                      meta: { hide: true, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                      components: {
                        default: () =>
                          import('../views/offer/transaction/OfferTransactionCashRevert.vue'),
                        header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                      },
                    },
                    {
                      name: 'offerTransactionRevertGiftCard',
                      path: 'revert-voucher',
                      meta: { hide: true, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                      components: {
                        default: () =>
                          import('../views/offer/transaction/OfferTransactionGiftCardRevert.vue'),
                        header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                      },
                    },
                    {
                      name: 'offerTransactionRevertExternalPayment',
                      path: 'revert-external-payment',
                      meta: { hide: true, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                      components: {
                        default: () =>
                          import('../views/offer/transaction/OfferTransactionExternalPaymentRevert.vue'),
                        header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                      },
                    },
                    {
                      name: 'offerTransactionRevertInternalLedgerCash',
                      path: 'revert-internal-cash',
                      meta: { hide: true, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                      components: {
                        default: () =>
                          import('../views/offer/transaction/OfferTransactionInternalLedgerCashRevert.vue'),
                        header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                      },
                    },
                  ],
                },
                {
                  name: 'offerReceipt',
                  path: 'receipt',
                  meta: { hide: true, excludeRoles: EXTERNAL_ROLES },
                  components: {
                    default: () => import('../views/offer/OfferReceipt.vue'),
                    footer: () => import('../views/footer/offer/OfferFooterReceipt.vue'),
                  },
                },
                {
                  name: 'offerDecline',
                  path: 'decline',
                  meta: { hide: true, excludeRoles: READONLY_ROLES },
                  components: {
                    default: () => import('../views/HomeView.vue'),
                  },
                },
                {
                  name: 'offerInvalidStatus',
                  path: 'invalid-status',
                  meta: { hide: true },
                  components: {
                    header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                    default: () => import('../views/offer/OfferInvalidStatusActions.vue'),
                    footer: () => import('../views/footer/GlobalFooterQuickNavigation.vue'),
                  },
                },
                {
                  name: 'offerRetrospect',
                  path: 'retrospect',
                  meta: { hide: true, horizontalScroll: true },
                  components: {
                    default: () => import('../views/offer/OfferSummary.vue'),
                    header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                    footer: () => import('../views/footer/offer/OfferFooterCompleted.vue'),
                  },
                },
              ],
            },
          ],
        },
        {
          name: 'handling',
          path: 'handling',
          meta: { icon: faSitemap, requiresRole: HANDLING_ROLES },
          children: [
            {
              name: 'handlingOverview',
              path: '',
              alias: 'overview',
              meta: { icon: faTableList, requiresRole: HANDLING_ROLES },
              components: {
                default: () => import('../views/handling/review/HandlingOverview.vue'),
                navbar: () => import('../views/navbar/SearchNavbar.vue'),
              },
            },
            {
              name: 'handlingLoose',
              path: 'loose',
              meta: { icon: faBarcode, requiresRole: HANDLING_ROLES, requiresSource: true },
              components: {
                default: () => import('../views/handling/loose/HandlingLooseItemSearch.vue'),
                navbar: () => import('../views/navbar/SearchNavbar.vue'),
              },
            },
            {
              name: 'handlingLooseItemHandling',
              path: 'loose/item/:handlingItemId',
              meta: { hide: true, requiresRole: HANDLING_ROLES, requiresSource: true },
              components: {
                default: () => import('../views/handling/item/HandlingItemHandling.vue'),
                footer: () =>
                  import('../views/footer/handling/HandlingFooterLooseItemHandling.vue'),
              },
            },
            {
              name: 'handlingItemDetails',
              path: 'item/:handlingItemId/detail',
              meta: { hide: true, requiresRole: HANDLING_ROLES },
              components: {
                header: () => import('../views/header/handling/HandlingHeaderItemDetails.vue'),
                default: () => import('../views/handling/item/HandlingItemDetails.vue'),
                footer: () => import('../views/footer/handling/HandlingFooterItemDetails.vue'),
              },
            },
            {
              name: 'handlingOffer',
              path: 'offer/:offerId',
              meta: { hide: true, requiresRole: HANDLING_ROLES },
              children: [
                {
                  name: 'handlingOfferPrepare',
                  path: 'prepare',
                  alias: '',
                  meta: { hide: true, requiresRole: HANDLING_ROLES, requiresSource: true },
                  components: {
                    header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                    default: () => import('../views/handling/prepare/HandlingOfferPrepare.vue'),
                    footer: () => import('../views/footer/handling/HandlingFooterOfferPrepare.vue'),
                  },
                },
                {
                  name: 'handlingOfferProcess',
                  path: 'process',
                  meta: { hide: true, requiresRole: HANDLING_ROLES, requiresSource: true },
                  components: {
                    header: () => import('../views/header/offer/OfferHeaderSummary.vue'),
                    default: () => import('../views/handling/prepare/HandlingOfferProcess.vue'),
                    footer: () => import('../views/footer/handling/HandlingFooterOfferProcess.vue'),
                  },
                },
                {
                  path: 'item/:itemId',
                  meta: { hide: true, requiresRole: HANDLING_ROLES },
                  children: [
                    {
                      name: 'handlingOfferPrepareItem',
                      path: '',
                      meta: { hide: true, requiresRole: HANDLING_ROLES, requiresSource: true },
                      components: {
                        default: () =>
                          import('../views/handling/prepare/HandlingOfferPrepareItem.vue'),
                        header: () =>
                          import('../views/header/handling/HandlingHeaderOfferItem.vue'),
                        navbar: () => import('../views/navbar/SearchNavbar.vue'),
                        footer: () =>
                          import('../views/footer/handling/HandlingFooterOfferPrepareItem.vue'),
                      },
                    },
                    {
                      name: 'handlingOfferItemHandling',
                      path: 'handling/:handlingItemId',
                      meta: { hide: true, requiresRole: HANDLING_ROLES, requiresSource: true },
                      components: {
                        default: () => import('../views/handling/item/HandlingItemHandling.vue'),
                        header: () =>
                          import('../views/header/handling/HandlingHeaderOfferItem.vue'),
                        footer: () =>
                          import('../views/footer/handling/HandlingFooterOfferPrepareItemHandling.vue'),
                      },
                    },
                  ],
                },
              ],
            },
            {
              name: 'handlingReview',
              path: 'review',
              meta: { icon: faEye, requiresRole: HANDLING_ROLES },
              components: {
                default: () => import('../views/handling/review/HandlingReviewOverview.vue'),
                navbar: () => import('../views/navbar/SearchNavbar.vue'),
              },
            },
          ],
        },
        {
          name: 'internalLedger',
          path: 'internal-ledger',
          meta: { icon: faCashRegister, excludeRoles: EXTERNAL_ROLES },
          children: [
            {
              name: 'internalLedgerOverview',
              path: 'ledger',
              meta: { icon: faTableList, requiresRole: ADMIN_ROLES },
              redirect: { name: 'internalLedgerSessions', params: { ledgerId: '-1' } },
              children: [
                {
                  name: 'internalLedgerSessions',
                  path: ':ledgerId/sessions',
                  meta: { hide: true },
                  components: {
                    default: () =>
                      import('../views/internal-ledger/InternalLedgerOverviewView.vue'),
                    navbar: () => import('../views/navbar/SearchNavbar.vue'),
                  },
                },
                {
                  name: 'internalLedgerSessionSummary',
                  path: ':ledgerId/sessions/:sessionId',
                  meta: { hide: true, excludeRoles: EXTERNAL_ROLES },
                  components: {
                    default: () =>
                      import('../views/internal-ledger/InternalLedgerSessionSummaryView.vue'),
                    navbar: () => import('../views/navbar/SearchNavbar.vue'),
                  },
                },
              ],
            },
            {
              name: 'internalLedgerCurrent',
              path: 'current',
              meta: { icon: faCashRegister, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
              children: [
                {
                  name: 'internalLedgerCurrentSelect',
                  path: 'select-ledger',
                  meta: { hide: true, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                  components: {
                    default: () =>
                      import('../views/internal-ledger/current/CurrentInternalLedgerSelectView.vue'),
                    footer: () =>
                      import('../views/footer/internal-ledger/InternalLedgerFooterSelect.vue'),
                  },
                },
                {
                  name: 'internalLedgerCurrentSession',
                  path: 'session',
                  meta: {
                    icon: faCashRegister,
                    excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES],
                  },
                  components: {
                    default: () =>
                      import('../views/internal-ledger/current/CurrentInternalLedgerSessionView.vue'),
                    footer: () =>
                      import('../views/footer/internal-ledger/InternalLedgerFooterSession.vue'),
                  },
                },
                {
                  name: 'internalLedgerCurrentCount',
                  path: 'count',
                  meta: { icon: faCoins, excludeRoles: [...READONLY_ROLES, ...EXTERNAL_ROLES] },
                  components: {
                    default: () =>
                      import('../views/internal-ledger/current/CurrentInternalLedgerCountView.vue'),
                    footer: () =>
                      import('../views/footer/internal-ledger/InternalLedgerFooterCount.vue'),
                  },
                },
              ],
            },
          ],
        },
        {
          name: 'quickTools',
          path: 'tools',
          meta: { icon: faToolbox },
          children: [
            {
              // The loose handling flow lives under handling; this is a second
              // way into it for users who look for it among the quick tools.
              name: 'quickToolsHandleItem',
              path: 'handle-item',
              meta: { icon: faBarcode, requiresRole: HANDLING_ROLES },
              redirect: { name: 'handlingLoose' },
            },
            {
              name: 'quickToolsPriceLookup',
              path: 'price-lookup',
              meta: { icon: faMagnifyingGlassDollar, excludeRoles: READONLY_ROLES },
              components: {
                default: () => import('../views/tools/QuickToolsPriceLookup.vue'),
                navbar: () => import('../views/navbar/SearchNavbar.vue'),
              },
            },
          ],
        },
        {
          name: 'data',
          path: 'data',
          meta: { icon: faChartLine, excludeRoles: EXTERNAL_ROLES },
          children: [
            {
              name: 'statistics',
              path: 'statistics',
              meta: { icon: faChartPie, excludeRoles: EXTERNAL_ROLES },
              children: [
                {
                  name: 'statisticsDashboard',
                  path: 'dashboard',
                  meta: { icon: faGaugeHigh, excludeRoles: EXTERNAL_ROLES },
                  components: {
                    default: () => import('../views/data/statistic/StatisticsDashboardView.vue'),
                  },
                },
                {
                  name: 'statisticsOffer',
                  path: 'offers',
                  meta: { icon: faFileInvoiceDollar, requiresRole: ADMIN_ROLES },
                  components: {
                    default: () => import('../views/data/statistic/StatisticsOfferView.vue'),
                  },
                },
                {
                  name: 'statisticsReview',
                  path: 'review',
                  meta: { icon: faEye, requiresRole: ADMIN_ROLES },
                  components: {
                    default: () => import('../views/data/statistic/StatisticsReviewView.vue'),
                  },
                },
                {
                  name: 'statisticsHandling',
                  path: 'handling',
                  meta: { icon: faSitemap, requiresRole: ADMIN_ROLES },
                  components: {
                    default: () => import('../views/data/statistic/StatisticsHandlingView.vue'),
                  },
                },
                {
                  name: 'statisticsTransaction',
                  path: 'transactions',
                  meta: { icon: faMoneyBillTransfer, requiresRole: STORE_MANAGER_ROLES },
                  components: {
                    default: () => import('../views/data/statistic/StatisticsTransactionView.vue'),
                  },
                },
                {
                  name: 'statisticsCash',
                  path: 'cash',
                  meta: { icon: faCashRegister, requiresRole: STORE_MANAGER_ROLES },
                  components: {
                    default: () => import('../views/data/statistic/StatisticsCashView.vue'),
                  },
                },
              ],
            },
            {
              name: 'export',
              path: 'export',
              meta: { icon: faCloudArrowDown, requiresRole: ADMIN_ROLES },
              children: [
                {
                  name: 'exportItemData',
                  path: 'items',
                  meta: {
                    icon: faTableList,
                    requiresRole: ADMIN_ROLES,
                    fullHeight: true,
                    width: 'w-full max-w-[2160px] px-4',
                  },
                  components: {
                    default: () => import('../views/data/export/ExportItemDataView.vue'),
                    navbar: () => import('../views/navbar/SearchNavbar.vue'),
                  },
                },
              ],
            },
          ],
        },
        {
          name: 'settings',
          path: 'settings',
          meta: { icon: faGears },
          children: [
            {
              name: 'settingsGlobal',
              path: 'global',
              meta: { icon: faSliders, requiresRole: ADMIN_ROLES },
              components: {
                default: () => import('../views/settings/SettingsView.vue'),
              },
            },
            {
              // Grouping route: no view of its own, it bundles everything that
              // is configured per source.
              name: 'settingsSource',
              path: 'source',
              meta: { icon: faStore, requiresRole: ADMIN_ROLES },
              children: [
                {
                  name: 'settingsSources',
                  path: 'list',
                  meta: { icon: faShop, requiresRole: ADMIN_ROLES },
                  components: {
                    default: () => import('../views/settings/SourcesSettingsView.vue'),
                    navbar: () => import('../views/navbar/SearchNavbar.vue'),
                  },
                },
                {
                  name: 'settingsPosDevices',
                  path: 'devices',
                  meta: { icon: faDesktop, requiresRole: STORE_MANAGER_ROLES },
                  components: {
                    default: () => import('../views/settings/PosDevicesSettingsView.vue'),
                    navbar: () => import('../views/navbar/SearchNavbar.vue'),
                  },
                },
                {
                  name: 'settingsInternalLedgers',
                  path: 'ledger',
                  meta: { icon: faCashRegister, requiresRole: ADMIN_ROLES },
                  components: {
                    default: () => import('../views/settings/InternalLedgersSettingsView.vue'),
                    navbar: () => import('../views/navbar/SearchNavbar.vue'),
                  },
                },
              ],
            },
            {
              name: 'settingsOffer',
              path: 'offer',
              meta: { icon: faBoxesStacked, requiresRole: ADMIN_ROLES },
              children: [
                {
                  name: 'settingsMarkupCampaigns',
                  path: 'markup-campaigns',
                  meta: { icon: faTags, requiresRole: ADMIN_ROLES },
                  components: {
                    default: () => import('../views/settings/MarkupCampaignsSettingsView.vue'),
                    navbar: () => import('../views/navbar/SearchNavbar.vue'),
                  },
                },
              ],
            },
            {
              // Personal notification settings sit here rather than under the
              // user group: every user manages their own, while the user group
              // stays gated to the roles that administer other people.
              name: 'settingsNotification',
              path: 'notification',
              meta: { icon: faBell },
              children: [
                {
                  name: 'settingsPushNotifications',
                  path: 'subscriptions',
                  meta: { icon: faInbox },
                  components: {
                    default: () => import('../views/settings/PushNotificationSettingsView.vue'),
                  },
                },
                {
                  // Mirrors `@RequiresRoles(ADMIN)` on the endpoint - a broadcast
                  // reaches every subscriber of the picked reason at once.
                  name: 'settingsPushNotificationBroadcast',
                  path: 'broadcast',
                  meta: { icon: faBullhorn, requiresRole: ADMIN_ROLES },
                  components: {
                    default: () => import('../views/settings/PushNotificationBroadcastView.vue'),
                  },
                },
              ],
            },
            {
              // Grouping route: it carries no view of its own, it only bundles
              // the account-facing screens into one submenu. The role is the
              // widest one any child allows, so the group never renders empty.
              name: 'settingsUser',
              path: 'user',
              meta: { icon: faUsersGear, requiresRole: STORE_MANAGER_ROLES },
              children: [
                {
                  name: 'settingsUsers',
                  path: 'accounts',
                  meta: { icon: faUsers, requiresRole: STORE_MANAGER_ROLES },
                  components: {
                    default: () => import('../views/settings/UsersSettingsView.vue'),
                    navbar: () => import('../views/navbar/SearchNavbar.vue'),
                  },
                },
                {
                  name: 'settingsApiClients',
                  path: 'api-clients',
                  meta: { icon: faPlug, requiresRole: ADMIN_ROLES },
                  components: {
                    default: () => import('../views/settings/ApiClientsSettingsView.vue'),
                  },
                },
              ],
            },
            {
              // Everything here is stored on the device, not on the server, so
              // it sits apart from - and below - the shared settings.
              name: 'settingsLocal',
              path: 'local',
              meta: { icon: faLaptop },
              children: [
                {
                  name: 'settingsScanner',
                  path: 'scanner',
                  meta: { icon: faBarcode },
                  components: {
                    default: () => import('../views/settings/ScannerSettingsView.vue'),
                  },
                },
                {
                  name: 'settingsPrinter',
                  path: 'printer',
                  meta: { icon: faPrint },
                  components: {
                    default: () => import('../views/settings/PrinterSettingsView.vue'),
                  },
                },
              ],
            },
          ],
        },
        {
          name: 'updatePassword',
          path: 'update-password',
          // A user on a shared device has no business changing an account
          // password; PIN users manage their PIN via update-pin instead.
          meta: { icon: faKey, section: 'bottom', hideInPos: true },
          component: () => import('../views/user/UpdatePassword.vue'),
        },
        {
          name: 'updatePin',
          path: 'update-pin',
          meta: { icon: faKey, section: 'bottom' },
          component: () => import('../views/user/UpdatePin.vue'),
        },
        {
          name: 'posRegister',
          path: 'pos/register',
          meta: {
            icon: faCashRegister,
            section: 'bottom',
            requiresRole: STORE_MANAGER_ROLES,
            hideInPos: true,
          },
          component: () => import('../views/pos/PosRegisterView.vue'),
        },
        {
          name: 'posExit',
          path: 'pos/exit',
          // Checking the device back out is a manager's call, not a user's,
          // even though both stand at the same device.
          meta: {
            icon: faShopSlash,
            section: 'bottom',
            textOnly: true,
            requiresRole: STORE_MANAGER_ROLES,
            posOnly: true,
          },
          component: () => import('../views/pos/PosExitView.vue'),
        },
        {
          name: 'logout',
          path: 'logout',
          meta: { icon: faRightFromBracket, section: 'bottom' },
          component: () => import('../views/LogoutView.vue'),
        },
      ],
    },
    {
      name: 'login',
      path: '/login',
      meta: { public: true },
      component: () => import('../views/LoginView.vue'),
    },
    {
      name: 'pinLogin',
      path: '/pos/pin',
      meta: { public: true },
      component: () => import('../views/pos/PinLoginView.vue'),
    },
  ],
});

router.beforeEach(function (to, from) {
  const { requiresRole = [], excludeRoles = [] } = to.meta;
  const { isLoggedIn, roles } = useAuth({ global: true });
  const { isPosMode } = usePos();

  routeLoading.value = true;

  if (to.meta.hideInPos && isPosMode.value) {
    // Route is off-limits on a checked-in device, even via a direct link.
    return { path: '/' };
  } else if (to.meta.posOnly && !isPosMode.value) {
    // Nothing to act on without a checked-in device - e.g. exiting POS mode.
    return { path: '/' };
  } else if (
    (to.name === 'login' || to.path === '/login') &&
    isPosMode.value &&
    !isLoggedIn.value
  ) {
    // A checked-in device is a keypad-only device: the password form only
    // reappears once the device is checked out (isPosMode false).
    return { name: 'pinLogin', query: to.query };
  } else if (!to.meta.public && !isLoggedIn.value) {
    // On a checked-in device only the user is missing, so ask for a PIN
    // rather than a password.
    if (isPosMode.value) return { name: 'pinLogin', query: { redirect: to.fullPath } };

    return { path: '/login', query: { redirect: to.fullPath } };
  } else if (to.name === 'pinLogin' && !isPosMode.value) {
    // No device check-in, so there is nothing for a PIN to authorize.
    return isLoggedIn.value ? { name: 'posRegister' } : { path: '/login' };
  } else if (
    (to.path === '/login' || to.path === 'login' || to.name === 'pinLogin') &&
    isLoggedIn.value
  ) {
    return { path: '/' };
  } else if (requiresRole.length > 0 && !requiresRole.some((role) => roles.value.includes(role))) {
    return { path: '/forbidden' };
  } else if (excludeRoles.some((role) => roles.value.includes(role))) {
    return { path: '/forbidden' };
  } else {
    return true;
  }
});

router.afterEach(() => {
  routeLoading.value = false;
});

router.isReady().then(() => registerLoading(routeLoading));

export default router;
